import type { ProviderSetupSummary } from '@epilot/sdk/phone-integration'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { OrganizationSetup } from './organization-setup'
import { PHONE_PATH, phoneSetupPath } from './shared'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { Label } from '@/components/ui/label'
import { OrganizationPicker } from '@/modules/epilot-idp/organization-picker'
import { ErrorNotice } from '@/modules/epilot-idp/shared'
import { useQueryGetOrganizations } from '@/services/organization.service'
import { useProviderSetups } from '@/services/phone-integration.service'

type PhoneSetupRow = ProviderSetupSummary & { organization_name?: string }

/** A tenant created by hand at Babelforce has no setup record until the setup runs once. */
const statusLabel = (setup: ProviderSetupSummary) =>
  !setup.setup_recorded ? 'Setup not run' : setup.ready ? 'Ready' : 'Incomplete'

const columns: ColumnDef<PhoneSetupRow>[] = [
  {
    id: 'organization',
    // name and id, so the table's search finds an organization by either
    accessorFn: (row) =>
      row.organization_name
        ? `${row.organization_name} · ${row.org_id}`
        : row.org_id,
    header: 'Organization',
    cell: ({ row }) => (
      <>
        <Link className="underline" to={phoneSetupPath(row.original.org_id)}>
          {row.original.organization_name ?? row.original.org_id}
        </Link>
        {row.original.organization_name && (
          <span className="text-muted-foreground">
            {' '}
            · {row.original.org_id}
          </span>
        )}
      </>
    )
  },
  {
    id: 'tenant',
    // While loading, the DataTable fills itself with empty skeleton rows and still runs every
    // accessor over them (sorting, search, facets), so nothing nested may be dereferenced here.
    accessorFn: (row) => row.tenant?.name ?? '',
    header: 'Babelforce tenant'
  },
  {
    id: 'status',
    accessorFn: statusLabel,
    header: 'Status',
    cell: ({ getValue }) => (
      <Badge variant={getValue() === 'Ready' ? 'success' : 'outline'}>
        {getValue<string>()}
      </Badge>
    )
  },
  {
    accessorKey: 'sso_identity_client_id',
    header: 'SSO application',
    cell: ({ row }) => row.original.sso_identity_client_id ?? '—'
  },
  {
    accessorKey: 'last_setup_by',
    header: 'Last setup by',
    cell: ({ row }) => row.original.last_setup_by ?? '—'
  },
  {
    accessorKey: 'updated_at',
    header: 'Updated',
    cell: ({ row }) =>
      row.original.updated_at
        ? new Date(row.original.updated_at).toLocaleString()
        : '—'
  }
]

export const PhoneSetups = () => {
  const query = useProviderSetups()
  const { data: organizations } = useQueryGetOrganizations()
  const navigate = useNavigate()
  const rows = useMemo<PhoneSetupRow[]>(() => {
    const names = new Map(organizations?.map((org) => [org.id, org.name]))

    return (query.data ?? []).map((setup) => ({
      ...setup,
      organization_name: names.get(setup.org_id) || undefined
    }))
  }, [query.data, organizations])

  return (
    <div className="space-y-4 text-primary">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Phone setups</h1>
          <p className="text-sm text-muted-foreground">
            Organizations with a Babelforce sub-account. Set an organization up
            before it assigns any phone agent.
          </p>
        </div>
        <Button onClick={() => void query.refetch()} variant="outline">
          Refresh
        </Button>
      </div>
      <ErrorNotice
        message={
          query.error
            ? `Could not load the phone setups. ${query.error.message}`
            : undefined
        }
      />
      <DataTable
        allowCreate
        columns={columns}
        data={rows}
        defaultSorting={[{ id: 'organization', desc: false }]}
        // the search box covers this overview; a facet filter per column is noise
        hideColumnFilters
        isLoading={query.isPending}
        onRowClick={(row) => navigate(phoneSetupPath(row.original.org_id))}
        tableConfigName="phone-setups"
        toolbarProps={{
          filterPlaceholder: 'Search phone setups…',
          // CSV export and column toggling add nothing to this overview.
          exportButtonProps: { className: 'hidden' },
          showViewButton: false
        }}
      />
    </div>
  )
}

export const CreatePhoneSetup = () => {
  const [orgId, setOrgId] = useState('')
  const navigate = useNavigate()

  return (
    <div className="max-w-3xl space-y-6 text-primary">
      <Link className="text-sm underline" to={PHONE_PATH}>
        Back to phone setups
      </Link>
      <div>
        <h1 className="text-2xl font-semibold">Set up an organization</h1>
        <p className="text-sm text-muted-foreground">
          Prepares Babelforce for an organization: its sub-account, SCIM user
          provisioning and single sign-on with Epilot IdP. Run it before the
          organization assigns any phone agent.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone-organization">Organization</Label>
        <OrganizationPicker
          id="phone-organization"
          onChange={setOrgId}
          value={orgId}
        />
      </div>
      {orgId ? (
        <OrganizationSetup
          onSetUp={() => navigate(phoneSetupPath(orgId))}
          orgId={orgId}
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          Select an organization to set up its phone.
        </p>
      )}
    </div>
  )
}
